import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import DSIconButton from '../../../../design-system/DSIconButton';
import DSTypography from '../../../../design-system/Typography/DSTypography';
import AddIcon from '@mui/icons-material/Add';
import { fontColorContrast } from '../../../../utils';
import DSBox from '../../../../design-system/DSBox';
import DSColorPicker from '../../../../design-system/DSColorPicker';

interface ColorPickerButtonProps {
  color: string;
  onColorClick?: () => void;
  onColorChange: (color: string) => void;
  disabled?: boolean;
}

const ColorPickerButton = ({
  color,
  onColorClick,
  onColorChange,
  disabled = false,
}: ColorPickerButtonProps) => {
  const { spacing, palette } = useTheme();
  const [open, setOpen] = useState(false);

  const supportsEyeDropper = 'EyeDropper' in window;

  const handleClick = () => {
    if (!disabled) {
      setOpen(true);
      onColorClick?.();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const buttonStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: spacing(12),
    borderRadius: spacing(12.5),
    boxShadow:
      '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
    backgroundColor: color || palette.action.disabled,
    color: fontColorContrast(color),
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderColor: 'transparent',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      backgroundColor: color,
      border: '1px solid rgba(0, 0, 0, 0.23)',
      borderColor: disabled ? 'transparent' : palette.text.primary,
    },
  };

  const buttonContent = color ? (
    <DSTypography sx={{ color: fontColorContrast(color) }}>
      {color}
    </DSTypography>
  ) : (
    <AddIcon />
  );

  if (supportsEyeDropper) {
    return (
      <DSBox sx={{ position: 'relative' }}>
        <DSIconButton
          disabled={disabled}
          sx={buttonStyles}
          onClick={handleClick}
        >
          {buttonContent}
        </DSIconButton>
        <DSColorPicker
          color={color}
          onChange={onColorChange}
          open={open}
          onClose={handleClose}
          disabled={disabled}
        />
      </DSBox>
    );
  }

  return (
    <DSIconButton disabled={disabled} sx={buttonStyles} onClick={onColorClick}>
      {buttonContent}
      <input
        type="color"
        value={color || '#000010'}
        onChange={(e) => onColorChange(e.target.value)}
        disabled={disabled}
        style={{
          position: 'absolute',
          borderRadius: spacing(12.5),
          opacity: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          width: '100%',
          height: '100%',
        }}
      />
    </DSIconButton>
  );
};

export default ColorPickerButton;
